import { SetStateAction, useState } from 'react';
import { useSchedule } from '../../../context/ScheduleContext';
import { client as api } from '../../../services/api';
import Loading from '../../Loading';
import { Order, ScheduleDetails } from '../../../types/types';
import { pushToDataLayer } from '../../../types/gtm';
import { sleep } from '../../../utils/sleep';

interface ButtonSendDataProps {
  schedule: boolean;
  order: Order | null;
  scheduleInfos?: ScheduleDetails;
  isVideoconference: boolean;
  choiceAc: boolean;
  setScheduleInfos: React.Dispatch<SetStateAction<ScheduleDetails>>;
  setSchedule: React.Dispatch<SetStateAction<boolean>>;
  onSuccess: () => void;
}

const ButtonSendData = ({
  schedule,
  setSchedule,
  onSuccess,
  setScheduleInfos,
  isVideoconference,
  choiceAc,
  order,
}: ButtonSendDataProps) => {
  const { data } = useSchedule();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const isCompleted = data?.hora === '' || data?.hora === undefined;

  const handleSchedule = async () => {
    setLoading(true);
    try {
      if (!isVideoconference || choiceAc) {
        const response = await api.post(`/agendamento/agendar/${order.id}`, data);

        if (response.status === 200) {
          setScheduleInfos(response.data);
          setMessage(response.data.mensagem || 'Agendamento realizado com sucesso!');
          setSchedule(true);
          onSuccess();
          pushToDataLayer({
            event: 'select_content',
            content_type: 'validacao:confirmar-agendamento',
          });
        } else {
          setMessage(response.data.mensagem || 'Dados de agenda não encontrados na resposta');
        }
      } else {
        const schedulePayload = {
          data: data.data,
          hora: data.hora,
          pedido: order.id,
          status: 1,
          tipo: 1,
          posto: 0,
          contato: {
            nome: order?.historicoPessoaFisica?.nomeCliente || order?.email || '',
            telefone: '',
            email: order?.email || '',
          },
        };

        const response = await api.post(
          `/agendamento/video-conferencia/agendar/${order.id}`,
          schedulePayload
        );

        if (response.status === 200) {
          // Adiciona um pequeno atraso para dar tempo ao backend de processar o agendamento
          // antes de buscar o status. Isso ajuda a evitar condições de corrida.
          await sleep(2000); // 2 segundo de espera
          const infos = await api.get(`/agendamento/status/${order.id}`); // Busca o status atualizado
          setScheduleInfos(infos.data);
          setMessage('Agendamento realizado com sucesso!');
          setSchedule(true);
          onSuccess();
          pushToDataLayer({
            event: 'select_content',
            content_type: 'validacao:confirmar-agendamento',
          });
        } else {
          setMessage(response.data.message || 'Não foi possível realizar o agendamento.');
        }
      }
    } catch (error: any) {
      console.error('Erro ao realizar o agendamento:', error);
      setMessage(
        error.response?.data?.message || error.message || 'Ocorreu um erro. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        className="flex items-center justify-center mx-auto mt-5 mb-7 max-w-[95%] md:max-w-full w-full h-10 bg-ins-blue hover:bg-insHover-blue text-white text-sm font-bold border border-ins-blue md:border-0 rounded-lg transition-all duration-300 disabled:border-gray-500 disabled:bg-gray-500 disabled:text-white"
        onClick={handleSchedule}
        disabled={isCompleted || schedule}
      >
        Confirmar agendamento
      </button>
      {isLoading && <Loading loading={isLoading} />}
      {message && <p className="text-center text-sm text-feedback-negative">{message}</p>}
    </>
  );
};

export default ButtonSendData;
